<template>
  <div class="assistente">
    <!--
      Trilha à esquerda, em coluna: na horizontal ela quebrava em duas linhas
      assim que os títulos eram frases, e passo a passo com os passos embolados
      é pior que nenhum. Em coluna cada etapa tem a sua linha, e sobra a largura
      toda para o que importa, que é preencher.
    -->
    <ol class="trilha">
      <li
        v-for="(etapa, indice) in etapas"
        :key="etapa.chave"
        class="trilha__item"
        :class="{
          'trilha__item--atual': indice === atual,
          'trilha__item--feito': indice < atual,
          'trilha__item--livre': navegacaoLivre,
        }"
        @click="(navegacaoLivre || indice < atual) && irPara(indice)"
      >
        <span class="trilha__marca">
          <fa-icon v-if="indice < atual && !navegacaoLivre" icon="check" />
          <template v-else>{{ indice + 1 }}</template>
        </span>
        <span class="trilha__nome">{{ etapa.titulo }}</span>
      </li>
    </ol>

    <div class="painel">
      <div class="etapa">
        <div class="etapa__cabecalho">
          <h3 class="etapa__titulo">{{ etapaAtual.titulo }}</h3>
          <p v-if="etapaAtual.descricao" class="etapa__descricao">{{ etapaAtual.descricao }}</p>
          <LinkDoPasso
            v-if="etapaAtual.link"
            :href="etapaAtual.link"
            class="etapa__link"
          >
            {{ etapaAtual.linkRotulo || TEXTOS.ABRIR_NO_SERVICO }}
          </LinkDoPasso>
        </div>

        <!--
          Instruções que não têm campo — criar um usuário no parceiro, conferir
          um relatório depois — não viram etapa: virariam uma tela com um texto
          e um botão de continuar. Ficam aqui, como o que se faz antes.
        -->
        <div v-if="atual === 0 && instrucoes.length" class="instrucoes">
          <div class="instrucoes__titulo">Antes de começar</div>
          <ul class="instrucoes__lista">
            <li v-for="(instrucao, indice) in instrucoes" :key="indice">
              <strong>{{ instrucao.titulo }}</strong>
              <span v-if="instrucao.descricao"> — {{ instrucao.descricao }}</span>
              <LinkDoPasso v-if="instrucao.link" :href="instrucao.link">abrir</LinkDoPasso>
            </li>
          </ul>
        </div>

        <!--
          Conector que atende por mais de um serviço pergunta isso primeiro: os
          campos seguintes mudam conforme a resposta.
        -->
        <div v-if="etapaAtual.escolhaDeProvedor" class="provedores">
          <button
            v-for="opcao in provedores"
            :key="opcao.chave"
            type="button"
            class="provedor"
            :class="{ 'provedor--ativo': provedor && provedor.chave === opcao.chave }"
            @click="$emit('escolher-provedor', opcao.chave)"
          >
            <!--
              A marca do serviço vem antes do nome: quem escolhe reconhece o
              logotipo que viu no site de onde tirou a credencial antes de ler
              o nome que demos a ele aqui.
            -->
            <LogoIntegracao
              class="provedor__marca"
              tamanho="pequeno"
              :logo="(opcao.marca || {}).logo"
              :cor="(opcao.marca || {}).cor"
              :nome="opcao.nome"
            />

            <span class="provedor__texto">
              <span class="provedor__nome">{{ opcao.nome }}</span>
              <span v-if="opcao.descricao" class="provedor__descricao">{{ opcao.descricao }}</span>
            </span>
          </button>
        </div>

        <!-- Etapa das regras: quem a desenha é a tela que hospeda. -->
        <template v-else-if="etapaAtual.chave === 'regras'">
          <slot name="regras" />
        </template>

        <template v-else-if="etapaAtual.chave === 'permissoes'">
          <slot name="permissoes" />
        </template>

        <!-- Etapa do mapeamento: quem o desenha é a tela que hospeda. -->
        <template v-else-if="etapaAtual.chave === 'mapeamento'">
          <p v-if="etapaAtual.aindaNaoSalva" class="aviso-apos-salvar">
            <fa-icon icon="info-circle" />
            Esta parte abre assim que você concluir: o mapeamento pertence à
            integração, e ela ainda não existe.
          </p>
          <slot v-else name="mapeamento" />
        </template>

        <!--
          O conteúdo da etapa é o próprio formulário, filtrado pelo grupo. É o
          mesmo componente da tela completa: campo novo no manifesto aparece
          aqui sem ninguém tocar no assistente.
        -->
        <FormularioManifesto
          v-else
          :value="value"
          :provedor="provedor"
          :erros="erros"
          :url-webhook="urlWebhook"
          :eventos-webhook="eventosWebhook"
          :integracao-id="integracaoId"
          :conexao="conexao"
          :somente-grupo="etapaAtual.grupo"
          :conectar-ao-abrir="conectarAoAbrir"
          :slug="slug"
          :salvando="salvando"
          @input="$emit('input', $event)"
          @desconectar="$emit('desconectar')"
          @salvar-e-conectar="$emit('salvar-e-conectar', $event)"
          @retomada-cumprida="$emit('retomada-cumprida')"
        />
      </div>

      <!--
        O que o serviço respondeu à credencial. Vale mais que um campo
        preenchido: "123" preenche o campo e não abre porta nenhuma.
      -->
      <CAlert
        v-if="verificacao"
        show
        :color="verificacao.ok ? 'success' : (verificacao.podeSeguir ? 'warning' : 'danger')"
        class="verificacao"
      >
        <fa-icon :icon="verificacao.ok ? 'check-circle' : 'exclamation-circle'" />
        {{ verificacao.mensagem }}
        <CButton
          v-if="verificacao.podeSeguir"
          size="sm"
          color="warning"
          variant="outline"
          class="verificacao__seguir"
          @click="seguirSemConferir"
        >
          Seguir mesmo assim
        </CButton>
      </CAlert>

      <!--
        Configurada, a integração deixa de ter um caminho a percorrer: mexe-se
        numa parte e salva. Antes disso, o fim de cada etapa é continuar — e o
        salvar só no fim, porque antes dele não há o que salvar.
      -->
      <div class="acoes">
        <CButton
          v-if="atual > 0 && !navegacaoLivre"
          variant="outline"
          color="secondary"
          :disabled="salvando"
          @click="voltar"
        >
          Voltar
        </CButton>

        <span v-if="!navegacaoLivre" class="acoes__contador">
          Etapa {{ atual + 1 }} de {{ etapas.length }}
        </span>
        <span v-else class="acoes__contador">{{ TEXTOS.VALE_DEPOIS_DE_SALVAR }}</span>

        <CButton
          v-if="!navegacaoLivre && atual < etapas.length - 1"
          color="primary"
          :disabled="salvando || verificando"
          @click="avancar"
        >
          {{ verificando ? 'Verificando...' : 'Continuar' }}
          <fa-icon v-if="!verificando" icon="angle-right" />
        </CButton>

        <CButton v-else color="primary" :disabled="salvando" @click="$emit('concluir')">
          {{ salvando ? 'Salvando...' : (navegacaoLivre ? 'Salvar' : 'Concluir') }}
        </CButton>
      </div>

      <button type="button" class="alternar" @click="$emit('ver-tudo')">
        Prefiro ver todos os campos de uma vez
      </button>
    </div>
  </div>
</template>

<script>
import TEXTOS from '@/views/Integracoes/textos';
/*
 * Configuração guiada: uma parte por vez, com a explicação junto dos campos
 * que ela explica.
 *
 * A tela completa mostra tudo ao mesmo tempo e põe o texto numa coluna à
 * parte — quem conhece a integração acha o campo depressa, mas quem está
 * configurando pela primeira vez lê de um lado e preenche do outro.
 *
 * As etapas são os GRUPOS DE CAMPOS, não os passos do tutorial. Parece um
 * detalhe e não é: montadas pelos passos, um conector cujo tutorial fala de
 * coisas feitas fora daqui ganhava etapas sem nada para preencher ("Conecte a
 * conta do Google — nada a preencher aqui") enquanto o campo que resolvia
 * exatamente aquilo esperava três etapas adiante. Pelos grupos, toda etapa tem
 * o que fazer; o texto do passo que reivindicou aquele grupo entra como
 * explicação, e as instruções sem campo viram um "antes de começar".
 */
import { post } from '@/helpers/apiRequest';
import FormularioManifesto from './FormularioManifesto';
import LinkDoPasso from './LinkDoPasso.vue';
import LogoIntegracao from './LogoIntegracao.vue';

/*
 * Quanto se espera o serviço do parceiro responder à conferência da
 * credencial, em milissegundos.
 */
const ESPERA_DA_CREDENCIAL = 30000;

export default {
  name: 'AssistenteIntegracao',
  components: { FormularioManifesto, LinkDoPasso, LogoIntegracao },
  props: {
    value: { type: Object, default: () => ({}) },
    provedor: { type: Object, default: null },
    provedores: { type: Array, default: () => [] },
    passos: { type: Array, default: () => [] },
    erros: { type: Object, default: () => ({}) },
    urlWebhook: { type: String, default: '' },
    eventosWebhook: { type: Array, default: () => [] },
    integracaoId: { type: [Number, String], default: null },
    conexao: { type: Object, default: () => ({}) },
    conectarAoAbrir: { type: String, default: '' },
    salvando: { type: Boolean, default: false },
    temMapeamento: { type: Boolean, default: false },

    // Conector roteável: a configuração pode valer só em certos momentos.
    temRegras: { type: Boolean, default: false },
    temPermissoes: { type: Boolean, default: false },
    // Slug do conector: é por ele que a credencial é conferida antes de salvar.
    slug: { type: String, default: '' },
  },
  data() {
    return {
      // Exposto para o template: as frases que outras telas do módulo repetem.
      TEXTOS,
      atual: 0,
      verificando: false,
      verificacao: null,
      // A escolha de onde abrir é feita uma vez: depois dela, quem manda na
      // etapa é o usuário.
      posicionou: false,
    };
  },
  mounted() {
    this.comecarNaEtapaQueImporta();
  },
  watch: {
    /*
     * A escolha da etapa de abertura espera as duas coisas de que depende: a
     * integração (que diz se já está configurada) e as etapas (que vêm dos
     * grupos do provedor). As duas chegam por prop, e nem sempre juntas — no
     * primeiro desenho, observar só a integração deixava a escolha passar
     * quando ela chegava antes dos grupos.
     */
    navegacaoLivre() {
      this.comecarNaEtapaQueImporta();
    },
    'etapas.length'() {
      this.comecarNaEtapaQueImporta();
    },
  },
  computed: {
    etapas() {
      const etapas = [];

      if (this.provedores.length > 1) {
        etapas.push({
          chave: 'provedor',
          titulo: 'Escolha o serviço',
          descricao: 'Esta integração atende por mais de um. O que vem depois depende desta escolha.',
          escolhaDeProvedor: true,
        });
      }

      this.gruposDoProvedor.forEach((nome) => {
        const passo = this.passos.find((item) => item.grupo && item.grupo === nome);

        etapas.push({
          chave: 'grupo-' + (nome || 'geral'),
          titulo: (passo && passo.titulo) || nome || 'Preencha a configuração',
          descricao: (passo && passo.descricao) || '',
          link: passo && passo.link,
          // O passo pode nomear para onde o link leva: "Abrir os webhooks do RD
          // Station" diz o que o genérico não dizia — qual tela abrir lá.
          linkRotulo: passo && passo.link_rotulo,
          grupo: nome,
        });
      });

      /*
       * O mapeamento é etapa sempre que o conector tiver um, mesmo antes de
       * haver instância: o editor só existe depois de salvar, mas o usuário
       * precisa saber que esse passo vem — e que sem ele o documento não é
       * lido, que é o campo que decide se o cadastro acontece.
       */
      const passoMapeamento = this.passos.find((item) => item.grupo === 'mapeamento');

      if (passoMapeamento || this.temMapeamento) {
        etapas.push({
          chave: 'mapeamento',
          titulo: (passoMapeamento && passoMapeamento.titulo) || 'Diga de onde sai cada dado',
          descricao: (passoMapeamento && passoMapeamento.descricao) || '',
          aindaNaoSalva: !this.temMapeamento,
        });
      }

      /*
       * Quem pode usar. Estava só na tela completa, e quem instalava pelo
       * passo a passo terminava sem ver a pergunta — o que, numa integração
       * cobrada por uso, é a diferença entre liberar a equipe certa e liberar
       * todo mundo sem perceber.
       */
      if (this.temPermissoes) {
        const passoPermissoes = this.passos.find((item) => item.grupo === 'permissoes');

        etapas.push({
          chave: 'permissoes',
          titulo: (passoPermissoes && passoPermissoes.titulo) || 'Quem pode usar',
          descricao: (passoPermissoes && passoPermissoes.descricao)
            || 'Os perfis liberados para este recurso. Sem escolher nenhum, ele fica disponível para quem já tem acesso à tela onde ele aparece.',
        });
      }

      /*
       * Quando esta configuração vale. É a última etapa de propósito: a
       * pergunta só faz sentido depois de a configuração existir — e, em
       * conector de instância única, a resposta é sempre "sempre". Sem ela
       * aqui, quem instalava pelo passo a passo não tinha como definir a regra
       * sem trocar para a tela completa.
       */
      if (this.temRegras) {
        const passoRegras = this.passos.find((item) => item.grupo === 'regras');

        etapas.push({
          chave: 'regras',
          titulo: (passoRegras && passoRegras.titulo) || 'Quando usar esta configuração',
          descricao: (passoRegras && passoRegras.descricao)
            || 'Uma regra para cada momento em que ela é consultada. Sem regra, ela atende sempre, junto das outras cuja regra casar.',
        });
      }

      return etapas;
    },

    /*
     * Os passos que nenhum grupo de campos resolve: o que o usuário faz no
     * parceiro, antes ou depois. Aparecem como lista na primeira etapa.
     */
    instrucoes() {
      const comCampos = this.gruposDoProvedor;

      return this.passos.filter(
        (passo) => !passo.grupo || (!comCampos.includes(passo.grupo) && passo.grupo !== 'mapeamento')
      );
    },

    /*
     * A integração já existe? Então não há caminho a percorrer: todas as
     * partes ficam acessíveis e salva-se de onde estiver. Enquanto ela não
     * existe, a ordem importa — pular adiante deixaria para trás um campo
     * obrigatório sem o usuário ter visto que passou por ele.
     */
    navegacaoLivre() {
      return !!this.integracaoId;
    },

    etapaAtual() {
      return this.etapas[this.atual] || { titulo: '' };
    },

    /**
     * Esta etapa é de preenchimento de campos?
     *
     * Só as etapas de campo recebem a chave `grupo` em `etapas()` — provedor,
     * mapeamento, permissões e regras são empilhadas sem ela. O teste é de
     * presença, e não de valor: `(grupo || '')` casava `undefined` com a
     * string vazia do grupo sem nome, e com isso a etapa de escolha do serviço
     * cobrava os campos da etapa seguinte, que nem estão na tela — o usuário
     * lia "Preencha para continuar: Chave do site" e não conseguia avançar.
     */
    etapaDeCampos() {
      return typeof this.etapaAtual.grupo === 'string';
    },

    /**
     * Campos secretos desta etapa — é a presença deles que diz se há o que
     * conferir antes de seguir.
     */
    credenciaisDaEtapa() {
      if (!this.etapaDeCampos) {
        return [];
      }

      return ((this.provedor && this.provedor.campos) || []).filter(
        (campo) => campo.secreto && (campo.grupo || '') === this.etapaAtual.grupo
      );
    },

    /*
     * Há credencial preenchida para conferir?
     *
     * Nem toda é obrigatória: no RD Marketing o token só serve para buscar a
     * lista de campos, e a integração funciona sem ele. Conferir um campo vazio
     * devolveria "informe o token" e barraria o usuário numa etapa que ele tem
     * o direito de pular.
     */
    /**
     * Os campos obrigatórios desta etapa que ainda estão vazios, pelo rótulo
     * que o usuário vê — é por ele que a pessoa acha o campo na tela.
     */
    obrigatoriosVazios() {
      if (!this.etapaDeCampos) {
        return [];
      }

      return ((this.provedor && this.provedor.campos) || [])
        .filter((campo) => campo.obrigatorio && (campo.grupo || '') === this.etapaAtual.grupo)
        .filter((campo) => {
          const valor = this.value[campo.chave];

          return valor === undefined || valor === null || String(valor).trim() === '';
        })
        .map((campo) => campo.label || campo.chave);
    },

    temCredencialPreenchida() {
      return this.credenciaisDaEtapa.some((campo) => {
        const valor = this.value[campo.chave];

        return valor !== undefined && valor !== null && String(valor).trim() !== '';
      });
    },

    gruposDoProvedor() {
      const nomes = [];

      ((this.provedor && this.provedor.campos) || []).forEach((campo) => {
        const nome = campo.grupo || '';

        if (!nomes.includes(nome)) {
          nomes.push(nome);
        }
      });

      return nomes;
    },
  },
  methods: {
    async avancar() {
      // Sem serviço escolhido não há campo seguinte que faça sentido.
      if (this.etapaAtual.escolhaDeProvedor && !this.provedor) {
        return;
      }

      /*
       * Campo obrigatório é cobrado na etapa em que aparece, e não no salvar
       * do fim: descobrir na última etapa que faltou algo três etapas atrás
       * obriga a refazer o caminho para achar o quê.
       */
      const faltando = this.obrigatoriosVazios;

      if (faltando.length) {
        this.verificacao = {
          ok: false,
          mensagem: faltando.length === 1
            ? `Preencha "${faltando[0]}" para continuar.`
            : `Preencha para continuar: ${faltando.join(', ')}.`,
        };

        return;
      }

      /*
       * Etapa que pede credencial não passa sem o serviço confirmar que ela
       * vale. Era o furo que deixava "123" virar integração salva: tudo parecia
       * certo até o primeiro registro não chegar, dias depois.
       */
      if (this.temCredencialPreenchida && !(await this.conferirCredencial())) {
        return;
      }

      if (this.atual < this.etapas.length - 1) {
        this.atual += 1;
        this.verificacao = null;
      }
    },

    /**
     * Pergunta ao serviço se a credencial vale, com o que está no formulário.
     */
    async conferirCredencial() {
      this.verificando = true;
      this.verificacao = null;

      /*
       * A conferência bate num serviço de terceiro. Com o parceiro fora do ar
       * a resposta não vem, e sem prazo o botão ficava em "Verificando..."
       * para sempre — a única saída era recarregar a página e perder o que já
       * estava preenchido. Estourado o prazo, o guia segue: quem valida de
       * verdade é o servidor, no salvar.
       */
      const controlador = new AbortController();
      const prazo = setTimeout(() => controlador.abort(), ESPERA_DA_CREDENCIAL);

      try {
        const resposta = await post(
          `/admin/integracoes/catalogo/${this.slug}/testar-credencial`,
          {
            provedor: this.provedor && this.provedor.chave,
            valores: this.value,
          },
          { signal: controlador.signal }
        );

        this.verificacao = { ok: true, mensagem: resposta.message };

        return true;
      } catch (erro) {
        if (erro && erro.name === 'AbortError') {
          this.verificacao = {
            ok: false,
            mensagem: 'O serviço não respondeu a tempo. Você pode seguir — a credencial '
              + 'é conferida de novo ao salvar.',
            podeSeguir: true,
          };

          return false;
        }

        this.verificacao = { ok: false, mensagem: erro.message || 'Não foi possível conferir a credencial.' };

        return false;
      } finally {
        clearTimeout(prazo);
        this.verificando = false;
      }
    },

    /**
     * Segue sem a conferência, depois de ela não ter respondido a tempo.
     */
    seguirSemConferir() {
      this.verificacao = null;

      if (this.atual < this.etapas.length - 1) {
        this.atual += 1;
      }
    },

    voltar() {
      if (this.atual > 0) {
        this.atual -= 1;
      }
    },

    /**
     * Onde o passo a passo abre.
     *
     * Integração já configurada entra na última etapa — nela está o que se
     * revisita: o mapeamento de campos, ou o que o conector tiver de mais
     * substantivo. Começar na primeira obrigava a atravessar de novo as etapas
     * de credencial e endereço, que são de instalação e não mudam mais.
     *
     * Instalação nova continua começando do começo: ali a ordem é o roteiro.
     */
    comecarNaEtapaQueImporta() {
      if (this.posicionou || !this.navegacaoLivre || this.etapas.length < 2) {
        return;
      }

      this.posicionou = true;
      this.atual = this.etapas.length - 1;
    },

    irPara(indice) {
      this.atual = indice;
      this.verificacao = null;
    },
  },
};
</script>

<style scoped>
.verificacao__seguir {
  margin-left: 12px;
}

.assistente {
  display: grid;
  gap: 28px;
  grid-template-columns: 236px minmax(0, 1fr);
}

@media (max-width: 900px) {
  .assistente {
    grid-template-columns: minmax(0, 1fr);
  }
}

.trilha {
  display: flex;
  flex-direction: column;
  gap: 2px;
  list-style: none;
  margin: 0;
  padding: 0;
}

.trilha__item {
  align-items: flex-start;
  border-radius: 8px;
  color: #98a2b3;
  display: flex;
  font-size: 13px;
  gap: 10px;
  line-height: 1.4;
  padding: 9px 10px;
}

.trilha__item--feito,
.trilha__item--livre {
  color: #475467;
  cursor: pointer;
}

.trilha__item--feito:hover,
.trilha__item--livre:hover {
  background: #f4f6f9;
}

.trilha__item--atual {
  background: #eef0f8;
  color: #3a3e81;
  font-weight: 600;
}

.trilha__marca {
  align-items: center;
  background: #eef0f4;
  border-radius: 50%;
  color: #667085;
  display: inline-flex;
  flex: 0 0 22px;
  font-size: 11px;
  height: 22px;
  justify-content: center;
  width: 22px;
}

.trilha__item--atual .trilha__marca {
  background: #3a3e81;
  color: #ffffff;
}

.trilha__item--feito .trilha__marca {
  background: #e3f5ea;
  color: #1a8a4a;
}

.etapa {
  background: #ffffff;
  border: 1px solid #e4e7ec;
  border-radius: 10px;
  padding: 26px 28px;
}

.etapa__cabecalho {
  border-bottom: 1px solid #f2f4f7;
  margin-bottom: 22px;
  padding-bottom: 18px;
}

.etapa__titulo {
  color: #1d2939;
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 6px;
}

.etapa__descricao {
  color: #667085;
  font-size: 13.5px;
  line-height: 1.55;
  margin: 0;
  max-width: 68ch;
}

.etapa__link {
  color: #2298a3;
  display: inline-block;
  font-size: 13px;
  margin-top: 10px;
}

.instrucoes {
  background: #f8f9fb;
  border: 1px solid #eceff3;
  border-radius: 8px;
  margin-bottom: 22px;
  padding: 14px 16px;
}

.instrucoes__titulo {
  color: #475467;
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.04em;
  margin-bottom: 8px;
  text-transform: uppercase;
}

.instrucoes__lista {
  color: #667085;
  font-size: 13px;
  line-height: 1.55;
  margin: 0;
  padding-left: 18px;
}

.instrucoes__lista strong {
  color: #344054;
  font-weight: 600;
}

.instrucoes__lista a {
  color: #2298a3;
  margin-left: 6px;
}

.provedores {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
}

.provedor {
  align-items: flex-start;
  background: #ffffff;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  gap: 12px;
  padding: 14px 16px;
  text-align: left;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.provedor__marca {
  flex: 0 0 auto;
}

.provedor__texto {
  min-width: 0;
}

.provedor:hover {
  border-color: #c7cace;
}

.provedor--ativo {
  border-color: #3a3e81;
  box-shadow: 0 0 0 1px #3a3e81;
}

.provedor__nome {
  color: #1d2939;
  display: block;
  font-size: 14px;
  font-weight: 600;
}

.provedor__descricao {
  color: #667085;
  display: block;
  font-size: 12px;
  margin-top: 4px;
}

.verificacao {
  margin: 16px 0 0;
}

.aviso-apos-salvar {
  background: #fffaeb;
  border: 1px solid #fdeec5;
  border-radius: 8px;
  color: #7a5d12;
  font-size: 13px;
  line-height: 1.55;
  margin: 0;
  padding: 14px 16px;
}

.acoes {
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 18px;
}

.acoes__contador {
  color: #98a2b3;
  font-size: 12px;
  margin-right: auto;
}

.alternar {
  background: none;
  border: 0;
  color: #667085;
  cursor: pointer;
  display: block;
  font-size: 12px;
  margin: 16px auto 0;
  padding: 4px;
  text-decoration: underline;
}

.alternar:hover {
  color: #3a3e81;
}
</style>
